import axios from "./axiosInstance";
import config from "./config";
import { getTenantId, getAuthSource } from "./apiHelpers";

const BASE_URL = config.BASE_URL;

const headers = (token: string | null, extra: Record<string, string> = {}) => ({
  "Content-Type": "application/json",
  authorization: `Bearer ${token}`,
  ...extra,
});

const publicHeaders = (token?: string | null) => ({
  "Content-Type": "application/json",
  "x-tenant-id": getTenantId(),
  ...(token ? { authorization: `Bearer ${token}`, "x-auth-source": getAuthSource() } : {}),
});

export interface VolunteerRole {
  id: string;
  name: string;
  description: string | null;
  start_at: string | null;
  end_at: string | null;
  capacity: number | null;
  sort_order: number;
  status: "active" | "cancelled";
  signup_count: number;
  waitlist_count: number;
  available: number | null;
  full: boolean;
}

export interface VolunteerSignup {
  id: string;
  role_id: string;
  user_id: string | null;
  membership_id: string | null;
  guest_first_name: string | null;
  guest_last_name: string | null;
  guest_email: string | null;
  guest_phone: string | null;
  status: string;
  notes: string | null;
  signed_up_at: string;
  confirmed_at: string | null;
  cancelled_at: string | null;
  reminder_sent_at: string | null;
  display_name?: string;
  display_email?: string;
}

// ---------- Admin ----------

export const listRoles = async (token: string | null, eventId: string): Promise<VolunteerRole[]> => {
  try {
    const res = await axios.get(`${BASE_URL}/events/${eventId}/volunteer-roles`, { headers: headers(token) });
    return res?.data?.roles || [];
  } catch {
    return [];
  }
};

export const createRole = async (
  token: string | null,
  eventId: string,
  body: { name: string; description?: string; start_at?: string; end_at?: string; capacity?: number; sort_order?: number }
) => {
  try {
    const res = await axios.post(`${BASE_URL}/events/${eventId}/volunteer-roles`, body, { headers: headers(token) });
    return res?.data || null;
  } catch (err: any) {
    return { success: false, error: err?.response?.data?.error || err?.message || "Create failed" };
  }
};

export const updateRole = async (
  token: string | null,
  eventId: string,
  roleId: string,
  body: Partial<VolunteerRole>
) => {
  try {
    const res = await axios.patch(`${BASE_URL}/events/${eventId}/volunteer-roles/${roleId}`, body, { headers: headers(token) });
    return res?.data || null;
  } catch (err: any) {
    return { success: false, error: err?.response?.data?.error || err?.message || "Update failed" };
  }
};

export const deleteRole = async (token: string | null, eventId: string, roleId: string) => {
  try {
    const res = await axios.delete(`${BASE_URL}/events/${eventId}/volunteer-roles/${roleId}`, { headers: headers(token) });
    return res?.data || null;
  } catch {
    return null;
  }
};

export const listSignups = async (token: string | null, eventId: string, filters: { role_id?: string; status?: string } = {}): Promise<VolunteerSignup[]> => {
  const qs = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => v && qs.set(k, v));
  try {
    const res = await axios.get(
      `${BASE_URL}/events/${eventId}/volunteer-signups${qs.toString() ? `?${qs}` : ""}`,
      { headers: headers(token) }
    );
    return res?.data?.signups || [];
  } catch {
    return [];
  }
};

export const confirmSignup = (token: string | null, eventId: string, signupId: string) =>
  axios.post(`${BASE_URL}/events/${eventId}/volunteer-signups/${signupId}/confirm`, {}, { headers: headers(token) }).then((r) => r?.data).catch(() => null);

export const markNoShow = (token: string | null, eventId: string, signupId: string) =>
  axios.post(`${BASE_URL}/events/${eventId}/volunteer-signups/${signupId}/no-show`, {}, { headers: headers(token) }).then((r) => r?.data).catch(() => null);

export const adminCancelSignup = (token: string | null, eventId: string, signupId: string) =>
  axios.delete(`${BASE_URL}/events/${eventId}/volunteer-signups/${signupId}`, { headers: headers(token) }).then((r) => r?.data).catch(() => null);

// ---------- Public / member-facing ----------

export const fetchPublicVolunteerRoles = async (token: string | null, eventId: string) => {
  try {
    const res = await axios.get(`${BASE_URL}/public/events/${eventId}/volunteer-roles`, { headers: publicHeaders(token) });
    return res?.data || null;
  } catch (err: any) {
    return { success: false, error: err?.response?.data?.error || err?.message || "Fetch failed" };
  }
};

export const submitVolunteerSignup = async (
  token: string | null,
  eventId: string,
  body: {
    role_id: string;
    guest_first_name?: string;
    guest_last_name?: string;
    guest_email?: string;
    guest_phone?: string;
    notes?: string;
  }
) => {
  try {
    const res = await axios.post(`${BASE_URL}/public/events/${eventId}/volunteer-signups`, body, { headers: publicHeaders(token) });
    return res?.data || null;
  } catch (err: any) {
    return { success: false, error: err?.response?.data?.error || err?.message || "Signup failed" };
  }
};

export const cancelVolunteerSignupByToken = async (eventId: string, cancellationToken: string) => {
  try {
    const res = await axios.post(
      `${BASE_URL}/public/events/${eventId}/volunteer-signups/cancel`,
      { token: cancellationToken },
      { headers: publicHeaders() }
    );
    return res?.data || null;
  } catch (err: any) {
    return { success: false, error: err?.response?.data?.error || err?.message || "Cancel failed" };
  }
};

export const fetchMyVolunteerSignups = async (token: string | null, eventId: string) => {
  try {
    const res = await axios.get(`${BASE_URL}/public/events/${eventId}/volunteer-signups/me`, { headers: publicHeaders(token) });
    return res?.data?.signups || [];
  } catch {
    return [];
  }
};

export const swapVolunteerSignup = async (
  token: string | null,
  eventId: string,
  signupId: string,
  body: { target_role_id: string; cancellation_token?: string }
) => {
  try {
    const res = await axios.post(
      `${BASE_URL}/public/events/${eventId}/volunteer-signups/${signupId}/swap`,
      body,
      { headers: publicHeaders(token) }
    );
    return res?.data || null;
  } catch (err: any) {
    return { success: false, error: err?.response?.data?.error || err?.message || "Swap failed" };
  }
};

export interface MyVolunteerSignup {
  id: string;
  event_id: string;
  role_id: string;
  status: string;
  signed_up_at: string;
  confirmed_at: string | null;
  cancelled_at: string | null;
  reminder_sent_at: string | null;
  notes: string | null;
  cancellation_token: string;
  event_name: string | null;
  event_dates: any[] | null;
  role_name: string | null;
  role_start_at: string | null;
  role_end_at: string | null;
}

export const fetchAllMyVolunteerSignups = async (token: string | null): Promise<MyVolunteerSignup[]> => {
  try {
    const res = await axios.get(
      `${BASE_URL}/public/events/volunteer-signups/me/all`,
      { headers: publicHeaders(token) }
    );
    return res?.data?.signups || [];
  } catch {
    return [];
  }
};
